import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import usePermissions from '../../common/usePermissions';
import { rootUrl } from '../../../config/config';
import Alerty from '../../common/Alerty';
import ConfirmModal from '../../common/ConfirmModal';
import ExpenseDetails from './ExpenseDetails';

const ExpenseDetailPage = () => {
    const { expenseId } = useParams();
    const navigate = useNavigate();
    const { token, user } = useAuth();
    const { hasPermission } = usePermissions();

    const [expense, setExpense] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ color: '', message: '' });

    const authHeaders = React.useMemo(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token]
    );

    const showAlert = (color, message) => {
        setAlert({ color, message });
        setOpen(true);
    };

    const fetchExpense = useCallback(async () => {
        if (!expenseId) {
            setError('Expense ID is missing');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.get(`${rootUrl}/api/expenses/${expenseId}`, { headers: authHeaders });
            if (response.data?.success) {
                setExpense(response.data.data || null);
            } else {
                setExpense(null);
                setError(response.data?.message || 'Expense not found');
            }
        } catch (err) {
            console.error('Error fetching expense details:', err);
            setExpense(null);
            setError(err.response?.data?.message || 'Failed to load expense');
        } finally {
            setLoading(false);
        }
    }, [authHeaders, expenseId]);

    useEffect(() => {
        fetchExpense();
    }, [fetchExpense]);

    const handleApprovalAction = async (id, status, notes = '') => {
        const response = await axios.put(
            `${rootUrl}/api/expenses/${id}/approval`,
            { approval_status: status, approval_notes: notes },
            { headers: authHeaders }
        );

        if (response.data?.success) {
            showAlert('success', `Expense ${status.toLowerCase()} successfully`);
            await fetchExpense();
            return response.data;
        }

        const msg = response.data?.message || 'Failed to update approval status';
        showAlert('error', msg);
        throw new Error(msg);
    };

    const handleReimbursementAction = async (id, status, notes = '') => {
        const response = await axios.put(
            `${rootUrl}/api/expenses/${id}/reimbursement`,
            { reimbursement_status: status, reimbursement_notes: notes },
            { headers: authHeaders }
        );

        if (response.data?.success) {
            showAlert('success', `Reimbursement ${status.toLowerCase()} successfully`);
            await fetchExpense();
            return response.data;
        }

        const msg = response.data?.message || 'Failed to update reimbursement status';
        showAlert('error', msg);
        throw new Error(msg);
    };

    const handleEditExpense = () => {
        if (!expense) return;
        navigate(`/expenses/${expense.expense_id || expense.id}/edit`);
    };

    const handleDeleteExpense = async () => {
        setDeleteLoading(true);
        setDeleteError('');
        try {
            const expId = expense?.expense_id || expense?.id;
            const response = await axios.delete(
                `${rootUrl}/api/expenses/${expId}`,
                { headers: authHeaders, data: { username: user?.username || user?.name || '' } }
            );
            if (response.data?.success) {
                setDeleteConfirm(false);
                navigate('/expenses');
            } else {
                setDeleteError(response.data?.message || 'Failed to delete expense');
            }
        } catch (err) {
            setDeleteError(err.response?.data?.message || 'Failed to delete expense');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleReverseExpense = async () => {
        if (!reverseNotes.trim()) { setReverseError('Reason is required'); return; }
        setReverseLoading(true);
        setReverseError('');
        try {
            const expId = expense?.expense_id || expense?.id;
            const response = await axios.post(
                `${rootUrl}/api/expenses/${expId}/reverse`,
                { reason: reverseNotes.trim() },
                { headers: authHeaders }
            );
            if (response.data?.success) {
                setReverseConfirm(false);
                fetchExpense();
            } else {
                setReverseError(response.data?.message || 'Failed to reverse expense');
            }
        } catch (err) {
            setReverseError(err.response?.data?.message || 'Failed to reverse expense');
        } finally {
            setReverseLoading(false);
        }
    };

    const canEditExpense = hasPermission('expenses.edit');
    const canCreateExpense = hasPermission('expenses.create');
    const canDeleteExpense = hasPermission('expenses.delete');
    const canReverseExpense = hasPermission('expenses.reverse');
    const canApproveExpense = hasPermission('expenses.approve');
    const canReimburseExpense = hasPermission('expenses.reimburse');

    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [reverseConfirm, setReverseConfirm] = useState(false);
    const [reverseLoading, setReverseLoading] = useState(false);
    const [reverseError, setReverseError] = useState('');
    const [reverseNotes, setReverseNotes] = useState('');

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <Alerty open={open} color={alert.color} message={alert.message} setOpen={setOpen} />

            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {loading && (
                    <div className="rounded-none border border-gray-200 bg-white p-6 text-center text-gray-600">
                        Loading expense details...
                    </div>
                )}

                {!loading && error && (
                    <div className="rounded-none border border-red-200 bg-red-50 p-6">
                        <h1 className="text-2xl font-bold text-red-700">Expense Not Found</h1>
                        <p className="mt-2 text-red-600">{error}</p>
                        <button
                            onClick={() => navigate('/expenses')}
                            className="mt-4 inline-flex items-center rounded-none bg-[#FF5733] px-4 py-2 text-white hover:bg-[#ff4520]"
                        >
                            Back to expenses
                        </button>
                    </div>
                )}

                {!loading && !error && expense && (
                    <ExpenseDetails
                        expense={expense}
                        asPage
                        onBack={() => navigate('/expenses')}
                        onCreateNew={canCreateExpense ? () => navigate('/expenses/new') : undefined}
                        onEdit={canEditExpense ? handleEditExpense : undefined}
                        onApprovalAction={canApproveExpense ? handleApprovalAction : undefined}
                        onReimbursementAction={canReimburseExpense ? handleReimbursementAction : undefined}
                        onDelete={canDeleteExpense && !expense?.is_reversed ? () => setDeleteConfirm(true) : undefined}
                        onReverse={canReverseExpense && expense?.approval_status === 'APPROVED' && !expense?.is_reversed ? () => setReverseConfirm(true) : undefined}
                    />
                )}
            </div>

            <ConfirmModal
                isOpen={deleteConfirm}
                title="Delete Expense"
                description={`Are you sure you want to delete ${expense?.expense_no}? This will post a reversal cashbook entry and cannot be undone.`}
                onCancel={() => { setDeleteConfirm(false); setDeleteError(''); }}
                onConfirm={handleDeleteExpense}
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />

            {reverseConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md mx-4">
                        <h2 className="text-lg font-bold text-gray-900 mb-1">Reverse Expense</h2>
                        <p className="text-sm text-gray-500 mb-4">Posts a counter-entry against <span className="font-semibold">{expense?.expense_no}</span>. The original record is preserved.</p>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Reason <span className="text-red-500">*</span></label>
                        <textarea
                            className="w-full border border-gray-300 rounded-lg p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-300"
                            rows={3}
                            placeholder="Why is this expense being reversed?"
                            value={reverseNotes}
                            onChange={(e) => { setReverseNotes(e.target.value); setReverseError(''); }}
                        />
                        {reverseError && <p className="text-red-500 text-xs mt-1">{reverseError}</p>}
                        <div className="flex justify-end gap-3 mt-4">
                            <button onClick={() => { setReverseConfirm(false); setReverseError(''); }} disabled={reverseLoading} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
                            <button onClick={handleReverseExpense} disabled={reverseLoading} className="px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50">
                                {reverseLoading ? 'Reversing…' : 'Reverse Expense'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ExpenseDetailPage;
